import { css } from "@emotion/css";
import type { EmptyStateOnboardingCallout, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Button, Collapse, ExternalLink, SearchInput } from "@octopusdeploy/design-system-components";
import { FilterIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentsSummaryResource, TenantResource, EnvironmentV2Resource, Repository, TagSetResource } from "@octopusdeploy/octopus-server-client";
import { getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import endpointRegistry from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import type { EndpointRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import ConfirmTentacleUpgradePanel from "~/areas/infrastructure/components/MachinesLayout/ConfirmTentacleUpgradePanel";
import { useOnClearMachineDispatch } from "~/areas/infrastructure/hooks/useOnClearMachineDispatch";
import { FilterBuilder } from "~/areas/tenants/components/Filtering/FilterBuilder/FilterBuilder";
import type { Filter, FilterValue } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import { getIncludedTagSetValues, createInclusiveOnlyEnvironmentFilter, createInclusiveOnlyTenantFilter, createInclusiveOnlyRoleFilter, createInclusiveOnlyMachineHealthFilter, createInclusiveOnlyDeploymentTargetTypeFilter, createInclusiveOnlyShellFilter, getIncludedEnvironmentValues, getIncludedTenantValues, getIncludedRoleValues, getIncludedMachineHealthValues, getIncludedDeploymentTargetTypeValues, getIncludedShellValues, FilterEntity, FilterOption, FilterMultiplicity, } from "~/areas/tenants/components/Filtering/FilterBuilder/filterBuilderTypes";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout/index";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import type { TagIndex } from "~/components/tagSets";
import * as tagSets from "~/components/tagSets";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper/index";
import { defaultDeploymentTargetFilter, createEnvironmentSummaryArgs } from "../EnvironmentsLayout/EnvironmentSummaryFilter";
import type { DeploymentTargetFilter } from "../EnvironmentsLayout/EnvironmentSummaryFilter";
import DeploymentTargetsSummarySection from "./DeploymentTargetsSummarySection";
import { KubernetesLiveStatusCallout } from "./KubernetesLiveStatusCallout";
interface DeploymentTargetsPageProps {
    spaceId: string;
    loaderData: DeploymentTargetsPageLoaderData;
    queryParams: DeploymentTargetsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<DeploymentTargetsPageQueryParams>;
}
export type DeploymentTargetsPageQueryParams = DeploymentTargetFilter;
export const deploymentTargetsPageTitle = "Deployment Targets";
interface DeploymentTargetsPageLoaderData {
    environments: EnvironmentV2Resource[];
    machineRoles: string[];
    tenants: TenantResource[];
    tagIndex: TagIndex;
    tenantTagSets: TagSetResource[];
    hasMachines: boolean;
    machineShellNames: string[];
    environmentsSummary: EnvironmentsSummaryResource;
    deploymentTargetRegistrations: EndpointRegistration[];
    targetTagSets: TagSetResource[];
}
export async function deploymentTargetsPageLoader(repository: Repository, queryParams: DeploymentTargetsPageQueryParams, ephemeralEnvironmentsAreEnabled: boolean): Promise<DeploymentTargetsPageLoaderData> {
    const defaultArgs = await createEnvironmentSummaryArgs(queryParams);
    const isTargetTagSetsEnabled = isFeatureToggleEnabled("TargetTagSetsFeatureToggle");
    const environmentsSummary = repository.Environments.summary(defaultArgs);
    const hasMachines = repository.Machines.list({ take: 0 }).then((result) => result.TotalResults > 0);
    const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(ephemeralEnvironmentsAreEnabled, repository);
    const machineShellNames = repository.MachineShells.all();
    const tagIndex = tagSets.getTagIndex(["Tenant"]);
    const targetRegistrations = endpointRegistry.getAllRegistrations();
    const tenants = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? await repository.Tenants.all() : [];
    const machineRoles = isTargetTagSetsEnabled ? Promise.resolve([]) : repository.MachineRoles.all();
    const targetTagSets = isTargetTagSetsEnabled ? repository.TagSets.getAllByScopes(["Target"]) : Promise.resolve([]);
    const tenantTagSets = repository.TagSets.getAllByScopes(["Tenant"]);
    return {
        environments: environments,
        machineRoles: await machineRoles,
        tenants,
        tagIndex: await tagIndex,
        tenantTagSets: await tenantTagSets,
        machineShellNames: await machineShellNames,
        deploymentTargetRegistrations: await targetRegistrations,
        hasMachines: await hasMachines,
        environmentsSummary: await environmentsSummary,
        targetTagSets: await targetTagSets,
    };
}
export function DeploymentTargetsPage({ spaceId, loaderData, queryParams, setQueryParams }: DeploymentTargetsPageProps) {
    const machineHealthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    const onClearMachine = useOnClearMachineDispatch();
    const showV2Page = useOctopusFeatureToggle("deployment-targets-page-uplift", false);
    const isTargetTagSetsEnabled = isFeatureToggleEnabled("TargetTagSetsFeatureToggle");
    const { doBusyTask, status } = useLegacyDoBusyTask();
    const navigate = useSpaceAwareNavigation();
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(queryParams.machinePartialName, (value) => setQueryParams((prev) => ({ ...prev, machinePartialName: value })), 200);
    const hasNoEnvironment = loaderData.environments.length === 0;
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const { result: summaries } = useQuery(async (repository) => {
        const args = await createEnvironmentSummaryArgs(queryParams, loaderData.tagIndex);
        return repository.Environments.summary(args);
    }, [queryParams, loaderData.tagIndex], "Load Environment Summaries", { initialResult: loaderData.environmentsSummary });
    useEffect(() => {
        onClearMachine();
    }, [onClearMachine]);
    const createTagFilters = useCallback((scope: "Target" | "Tenant", tagSets: TagSetResource[], selectedTagIds: string[]): Filter[] => {
        const filteredTagSets = tagSets.filter((ts) => ts.Type !== "FreeText");
        return filteredTagSets.map((tagSet) => {
            const tagIdsInTagSet = tagSet.Tags.map((t) => t.Id);
            const tagIdsInIncludeFilter = selectedTagIds.filter((t) => tagIdsInTagSet.includes(t));
            return {
                entity: FilterEntity.TagSet,
                options: [{ option: FilterOption.In, initialValue: tagIdsInIncludeFilter.length > 0 ? tagIdsInIncludeFilter : undefined }],
                multiplicity: FilterMultiplicity.OncePerOption,
                availableItems: tagSet.Tags,
                name: `${tagSet.Name} (${scope})`,
            };
        });
    }, []);
    const targetTagFilters = useMemo(() => {
        if (!isTargetTagSetsEnabled)
            return [];
        if (!loaderData.targetTagSets || loaderData.targetTagSets.length === 0)
            return [];
        return createTagFilters("Target", loaderData.targetTagSets, queryParams.targetTags || []);
    }, [isTargetTagSetsEnabled, loaderData.targetTagSets, queryParams.targetTags, createTagFilters]);
    const tenantTagFilters = useMemo(() => {
        if (!loaderData.tenantTagSets || loaderData.tenantTagSets.length === 0)
            return [];
        return createTagFilters("Tenant", loaderData.tenantTagSets, queryParams.tenantTags || []);
    }, [loaderData.tenantTagSets, queryParams.tenantTags, createTagFilters]);
    const environmentFilter = loaderData.environments?.length > 0 ? createInclusiveOnlyEnvironmentFilter(loaderData.environments, queryParams.environmentIds) : null;
    const tenantFilter = loaderData.tenants?.length > 0 ? createInclusiveOnlyTenantFilter(loaderData.tenants, queryParams.tenantIds) : null;
    const roleFilter = !isTargetTagSetsEnabled && loaderData.machineRoles?.length > 0 ? createInclusiveOnlyRoleFilter(loaderData.machineRoles, queryParams.roles) : null;
    const healthStatusFilter = machineHealthStatuses?.length > 0 ? createInclusiveOnlyMachineHealthFilter(machineHealthStatuses, queryParams.healthStatuses) : null;
    const deploymentTargetTypeFilter = loaderData.deploymentTargetRegistrations?.length > 0
        ? createInclusiveOnlyDeploymentTargetTypeFilter(loaderData.deploymentTargetRegistrations.map((r) => ({ Id: r.key, Name: r.name })), queryParams.deploymentTargetTypes)
        : null;
    const shellFilter = loaderData.machineShellNames?.length > 0 ? createInclusiveOnlyShellFilter(loaderData.machineShellNames, queryParams.shellNames) : null;
    const filterBuilderFilters: Filter[] = [environmentFilter, tenantFilter, roleFilter, healthStatusFilter, deploymentTargetTypeFilter, shellFilter, ...targetTagFilters, ...tenantTagFilters].filter((f): f is Filter => f !== null);
    const handleFilterChange = useCallback((filterValues: FilterValue[]) => {
        const environmentIds = getIncludedEnvironmentValues(filterValues);
        const tenantIds = getIncludedTenantValues(filterValues);
        const healthStatuses = getIncludedMachineHealthValues(filterValues);
        const deploymentTargetTypes = getIncludedDeploymentTargetTypeValues(filterValues);
        const shellNames = getIncludedShellValues(filterValues);
        const allSelectedTagIds = getIncludedTagSetValues(filterValues);
        const targetTagIds = (loaderData.targetTagSets || []).flatMap((ts) => ts.Tags.map((t) => t.Id));
        const tenantTagIds = (loaderData.tenantTagSets || []).flatMap((ts) => ts.Tags.map((t) => t.Id));
        const targetTags = allSelectedTagIds.filter((id) => targetTagIds.includes(id));
        const tenantTags = allSelectedTagIds.filter((id) => tenantTagIds.includes(id));
        const roles = getIncludedRoleValues(filterValues);
        setQueryParams((prev) => ({
            ...prev,
            roles,
            targetTags: isTargetTagSetsEnabled ? targetTags : [],
            tenantTags,
            environmentIds,
            tenantIds,
            healthStatuses,
            deploymentTargetTypes,
            shellNames,
        }));
    }, [isTargetTagSetsEnabled, setQueryParams, loaderData.targetTagSets, loaderData.tenantTagSets]);
    const handleToggleFilters = useCallback(() => {
        setFiltersExpanded(!filtersExpanded);
    }, [filtersExpanded]);
    const addDeploymentTargetPageAction: PrimaryPageAction = {
        type: "navigate",
        label: "Add Deployment Target",
        path: links.selectDeploymentTargetTypePage.generateUrl({ spaceId }),
        hasPermissions: isAllowed({
            permission: Permission.MachineCreate,
            environment: "*",
            tenant: "*",
        }),
        disabled: hasNoEnvironment,
        extraContext: hasNoEnvironment ? "Please create an environment before creating deployment target" : undefined,
    };
    const noEnvironmentOnboardingCallout: EmptyStateOnboardingCallout | undefined = loaderData.environments.length === 0
        ? {
            title: "Environments required",
            description: "Before you can create a deployment target, you will need to create at least one environment",
            action: {
                label: "Go to Environments",
                buttonType: "secondary",
                type: "navigate",
                path: links.environmentsPage.generateUrl({ spaceId }),
            },
        }
        : undefined;
    const callout = (<>
            {summaries.TentacleUpgradesRequired && (<ConfirmTentacleUpgradePanel doBusyTask={doBusyTask} onTentacleUpgradeComplete={() => {
                navigate.navigate(links.tasksPage.generateUrl());
            }}/>)}
            <KubernetesLiveStatusCallout machineEndpointSummaries={summaries.MachineEndpointSummaries}/>
        </>);
    if (!loaderData.hasMachines) {
        return (<PortalEmptyStateOnboarding title={"Configure your deployment targets"} description={"Deployment target represent the servers, machines and cloud services where software and services will be deployed."} primaryAction={addDeploymentTargetPageAction} learnMore={<>
                        <ExternalLink href="OnboardingDeploymentTargetsLearnMore">Learn more</ExternalLink>
                        <ExternalLink href={"OnboardingDeploymentTargetsVideo"}>Video (5 mins)</ExternalLink>
                    </>} callout={noEnvironmentOnboardingCallout}/>);
    }
    const filters = {
        inputs: [
            <DeploymentTargetsFilterSection key="deployment-targets-filter-section" filterBuilderFilters={filterBuilderFilters} handleFilterChange={handleFilterChange} filtersExpanded={filtersExpanded} onToggleFilters={handleToggleFilters} debouncedPartialName={debouncedPartialName ?? ""} setDebouncedPartialName={setDebouncedPartialName} queryParams={queryParams} setQueryParams={setQueryParams} hasUserSelectedValues={isFiltering(queryParams)} onResetFilter={() => setQueryParams(defaultDeploymentTargetFilter)}/>,
        ],
    };
    return (<PageContent header={{
            title: deploymentTargetsPageTitle,
            primaryAction: addDeploymentTargetPageAction,
        }} filters={filters} legacyStatus={status} callout={{
            type: "custom",
            content: callout,
        }}>
            <BorderedContent>
                <DeploymentTargetsSummarySection environmentsSummary={summaries} filter={queryParams} tenants={loaderData.tenants} tagIndex={loaderData.tagIndex} environments={loaderData.environments} showV2Page={showV2Page}/>
            </BorderedContent>
        </PageContent>);
}
function isFiltering(currentFilter: DeploymentTargetsPageQueryParams) {
    return !isEqual(currentFilter, defaultDeploymentTargetFilter);
}
interface DeploymentTargetsFilterSectionProps {
    filterBuilderFilters: Filter[];
    handleFilterChange: (filterValues: FilterValue[]) => void;
    filtersExpanded: boolean;
    onToggleFilters: () => void;
    debouncedPartialName: string;
    setDebouncedPartialName: (value: string) => void;
    queryParams: DeploymentTargetsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<DeploymentTargetsPageQueryParams>;
    hasUserSelectedValues: boolean;
    onResetFilter: () => void;
}
function DeploymentTargetsFilterSection({ filterBuilderFilters, handleFilterChange, filtersExpanded, onToggleFilters, debouncedPartialName, setDebouncedPartialName, queryParams, setQueryParams, hasUserSelectedValues, onResetFilter, }: DeploymentTargetsFilterSectionProps) {
    return (<section className={filterSectionStyles.filterSection} aria-label="deployment-targets-filter-selection">
            <div className={filterSectionStyles.filterControls}>
                <div className={filterSectionStyles.filterControlsLeft}>
                    <div className={filterSectionStyles.searchInputContainer}>
                        <SearchInput placeholder="Search by name" value={debouncedPartialName} onChange={setDebouncedPartialName} accessibleName="Search by deployment target name" autoFocus={true}/>
                    </div>
                    <Button label={`${filtersExpanded ? "Hide" : "Show"} advanced filters`} icon={<FilterIcon size={20}/>} onClick={onToggleFilters} importance="quiet"/>
                </div>
            </div>
            <Collapse in={filtersExpanded} timeout="auto" unmountOnExit>
                <div className={filterSectionStyles.advancedFiltersContainer}>
                    {filterBuilderFilters.length > 0 && (<div className={filterSectionStyles.filterBuilder}>
                            <FilterBuilder filters={filterBuilderFilters} onChange={handleFilterChange} display="full"/>
                        </div>)}
                    <div className={filterSectionStyles.staticFilters}>
                        <AdvancedFilterCheckbox label="Disabled only" value={queryParams.isDisabled ?? false} onChange={(x) => setQueryParams((prev) => ({ ...prev, isDisabled: x ? x : undefined }))}/>
                    </div>
                    {hasUserSelectedValues && (<div className={filterSectionStyles.resetButton}>
                            <Button label="Reset filters" onClick={onResetFilter} importance="quiet"/>
                        </div>)}
                </div>
            </Collapse>
        </section>);
}
const filterSectionStyles = {
    filterSection: css({
        width: "100%",
        marginBottom: space[16],
    }),
    filterControls: css({
        display: "flex",
        justifyContent: "flex-start",
        gap: space[8],
    }),
    filterControlsLeft: css({
        display: "flex",
        gap: space[16],
        flexWrap: "wrap",
        alignItems: "center",
    }),
    searchInputContainer: css({
        width: "19rem",
    }),
    advancedFiltersContainer: css({
        paddingTop: space[16],
    }),
    filterBuilder: css({
        marginBottom: space[16],
    }),
    staticFilters: css({
        marginBottom: space[16],
    }),
    resetButton: css({
        display: "flex",
        justifyContent: "flex-start",
    }),
};
